// Import components
import CreateClonableDataset from './CreateClonableDataset';
import { calculationsController } from './Utils/Calculations';

// Import utils
import { getChild, getChildren, sanitiseStringForID, identifyFields, getDocumentElements } from './Utils/Utils';

/**
 * Create the farm calculator.
 * 
 * @param {object} options
 * 
 * @returns {object}
 */
const CreateFarmCalcAccordion = options => {
    const FarmCalcAccordion = options.farmCalcAccordion;
    const Button = getChild(FarmCalcAccordion, options.selectors.button);
    const Container = getChild(FarmCalcAccordion, options.selectors.container);
    const DatasetsToClone = getChildren(FarmCalcAccordion, options.selectors.dataset);
    const Dropdown = getChild(FarmCalcAccordion, options.selectors.dropdown);
    const SectionNumber = getChild(FarmCalcAccordion, options.selectors.number);
    const Title = getChild(FarmCalcAccordion, options.selectors.title);
    const validationError = getChild(FarmCalcAccordion, options.selectors.error)
    const Nested = options.nested;
    const IsClone = options.isClone;
    let identifier = null;

    // If the cloned asset had a validation error, clear it
    if (validationError) {
        validationError.innerHTML = ''
    }

    /**
     * Toggle the dropdown depending on the value of show.
     * 
     * @param {boolean}     show
     * 
     * @returns {void}
     */
    const __toggleDropdown = show => {
        if (show) {
            Button.classList.add(options.classes.active);
            Dropdown.style.height = `${Container.offsetHeight}px`;

            setTimeout(() => {
                Dropdown.classList.add(options.classes.active);
                Dropdown.removeAttribute('style');
                Dropdown.removeAttribute('aria-hidden');
                Dropdown.removeAttribute('inert');
            }, options.animationTime);
        } else {
            Button.classList.remove(options.classes.active);
            Dropdown.style.height = `0px`;

            setTimeout(() => {
                if (!IsClone) {
                    Dropdown.setAttribute('aria-hidden', true);
                    Dropdown.setAttribute('inert', true);
                }
                Dropdown.removeAttribute('style');
                Dropdown.classList.remove(options.classes.active);
            }, options.animationTime);
        }

        Button.setAttribute('aria-expanded', show);
    };

    /**
     * Bind listeners and init other related components.
     * 
     * @returns {void}
     */
    const __bind = () => {
        Button.addEventListener('click', e => {
            e.preventDefault();
            __toggleDropdown(!Button.classList.contains(options.classes.active));
        });
    };

    /**
     * Set the aria control attributes to a hyphenated, sanitised
     * string based off the accordion button title.
     * 
     * @returns {void}
     */
    const __setIdAriaAttributes = () => {
        identifier = sanitiseStringForID(Title.innerText);

        // If we have an identifier and the current accordion is NOT a clone,
        // set the attributes we need. CreateClonableDataset sets its own custom
        // attributes for these elements and we don't want to override them.
        if (identifier.length > 0 && IsClone === false) {
            Button.setAttribute('aria-controls', identifier);
            Dropdown.setAttribute('id', identifier);
            Dropdown.setAttribute('inert', true);
        }
    };

    /**
     * Create the clonable dataset (aka accordion and fields).
     * 
     * @returns {void}
     */
    const __setClonableDataset = () => {
        if (DatasetsToClone && DatasetsToClone.length > 0) {
            DatasetsToClone.forEach(DatasetToClone => {
                const Dataset = new CreateClonableDataset({
                    dataset: DatasetToClone,
                    attributes: {
                        ariaControls: 'aria-controls',
                        id: 'id',
                        for: 'for',
                        mount: 'data-mount',
                        name: 'name',
                        number: 'data-number'
                    },
                    selectors: {
                        add: '.farm-calc-accordion__action--add',
                        button: options.selectors.button,
                        clonable: '.clonable',
                        dropdown: options.selectors.dropdown,
                        number: options.selectors.number,
                        remove: '.farm-calc-accordion__action--remove',
                        title: options.selectors.title
                    }
                });

                Dataset.init();
            });
        }
    };

    /**
     * Watch the accordion for ANY updates. Mark it as updated if the user
     * changes the input fields within the section.
     * 
     * @returns     {void}
     */
    const __watchForUpdate = () => {
        const Tags = ['INPUT', 'SELECT', 'TEXTAREA'];
        let fieldsToWatch = [];

        // Loop through the given field tabs and push them to our array.
        Tags.forEach(tag => {
            const Elements = getChildren(FarmCalcAccordion, tag);

            if (Elements) {
                Elements.forEach(El => {
                    fieldsToWatch.push(El);
                })
            }
        });

        // If we have any number of fields, apply the event listeners
        // and mark them as updated when the listener is activated.
        if (fieldsToWatch.length > 0) {
            fieldsToWatch.forEach(field => {
                field.addEventListener('change', () => {
                    FarmCalcAccordion.classList.add(options.classes.updated);
                });

                field.addEventListener('input', () => {
                    FarmCalcAccordion.classList.add(options.classes.updated);
                });
            });
        }
    };

    /**
     * Publicly exposed init function.
     * 
     * @returns {void}
     */
    const init = () => {
        if (SectionNumber &&
            Button &&
            Container &&
            Dropdown &&
            Title
        ) {
            if (!Nested) {
                SectionNumber.innerText = options.index;
            };

            // All accordions on page load are hidden by default (except clones)
            if (!IsClone) {
                Dropdown.setAttribute('aria-hidden', true);
            }

            // Set our attributes, bind listeners, create clonable datasets.
            __setIdAriaAttributes();
            __bind();
            __setClonableDataset();

            if (!IsClone) {
                // We need to add a simple event listener that watches for ANY field change,
                // it will then mark the accordion as 'updated' and make it green.
                __watchForUpdate();

                // We set this attribute on the top level accordions only,
                // purely to identify which cloned fields need to be initialised.
                FarmCalcAccordion.setAttribute(
                    options.attributes.id,
                    identifier
                );

                if (identifyFields(Container)) {
                    calculationsController(identifyFields(Container), identifier);
                }
            }
        } else {
            console.warn('Missing components for Farm Calculator accordion.');
        }
    };

    /**
     * Return frozen object with init function.
     */
    return Object.freeze({ init });
};

export default CreateFarmCalcAccordion;
